import { useMemo, useState } from "react";
import { CheckCircleRounded, GroupsRounded, PersonOffRounded, PersonRounded, SearchRounded, VerifiedUserRounded } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion, type Variants } from "framer-motion";

import { colors } from "@components/ui/colors";
import { MetricCard, metricsContainerAnimation } from "../components/MetricCard";

import { useSuperAdminUsersQuery } from "./hook/useGetUser";
import { SuperAdminUser } from "./interfaces";

type ConfirmationFilter = "all" | "confirmed" | "unconfirmed";

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionTableRow = motion(TableRow);

const pageAnimation: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

const filtersAnimation: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.12,
      duration: 0.28,
      ease: "easeOut",
    },
  },
};

const tableAnimation: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.16,
      duration: 0.32,
      ease: "easeOut",
    },
  },
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatRole = (role: string) => {
  const labels: Record<string, string> = {
    superadmin: "Super admin",
    admin: "Admin",
    employee: "Employee",
    client: "Client",
    business: "Business",
    virtualReceptionist: "Virtual receptionist",
    accountManager: "Account manager",
    other: "Other",
  };

  return labels[role] ?? role;
};

const getUserName = (user: SuperAdminUser) => {
  const alias = user.alias?.trim();

  if (user.useAliasAsDisplayName && alias) {
    return alias;
  }

  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email;
};

const getUserInitials = (user: SuperAdminUser) => {
  return getUserName(user)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const isUserConfirmed = (user: SuperAdminUser) => {
  return user.isEmailConfirmed || user.isPhoneConfirmed;
};

export const UserView = () => {
  const theme = useTheme();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [confirmationFilter, setConfirmationFilter] = useState<ConfirmationFilter>("all");
  const [roleFilter, setRoleFilter] = useState("all");
  type RecordTypeFilter = "all" | "users" | "workforces";

  const [recordTypeFilter, setRecordTypeFilter] = useState<RecordTypeFilter>("all");

  const { data, isLoading, isFetching, isError } = useSuperAdminUsersQuery({
    page: page + 1,
    pageSize,
    search,
    role: roleFilter,
    confirmationStatus: confirmationFilter,
    recordType: recordTypeFilter,
  });

  const users = data?.items ?? [];

  const availableRoles = useMemo(() => {
    return Array.from(new Set(users.flatMap((user) => user.roles.map((role) => role.type)))).sort();
  }, [users]);

  const metrics = data?.metrics;

  const loadingValue = <CircularProgress size={20} thickness={5} sx={{ color: colors.orange.base }} />;

  const columns = [
    {
      id: "user",
      label: "User",
      width: 120,
      align: "left",
    },
    {
      id: "email",
      label: "Email",
      width: 160,
      align: "left",
    },
    {
      id: "phone",
      label: "Phone",
      width: 90,
      align: "left",
    },
    {
      id: "roles",
      label: "Roles",
      width: 80,
      align: "left",
    },
    {
      id: "companies",
      label: "Companies",
      width: 100,
      align: "left",
    },
    {
      id: "status",
      label: "Account status",
      width: 80,
      align: "center",
    },
    {
      id: "createdAt",
      label: "Created",
      width: 90,
      align: "center",
    },
  ] as const;

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={pageAnimation}
      sx={{
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        px: { xs: 1.5, sm: 2 },
        pt: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <MotionBox variants={metricsContainerAnimation} initial="hidden" animate="visible" sx={{ mb: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard
                label="Users"
                value={isLoading ? loadingValue : (metrics?.totalUsers ?? 0)}
                helper="Registered accounts"
                icon={<GroupsRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard
                label="Confirmed"
                value={isLoading ? loadingValue : (metrics?.confirmedUsers ?? 0)}
                helper="Verified by email or phone"
                icon={<VerifiedUserRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard
                label="Owners"
                value={isLoading ? loadingValue : (metrics?.companyOwners ?? 0)}
                helper="Company owners"
                icon={<PersonRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2.4}>
              <MetricCard
                label="With account"
                value={isLoading ? loadingValue : (metrics?.linkedWorkforces ?? 0)}
                helper="Workforce linked to users"
                icon={<CheckCircleRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2.4}>
              <MetricCard
                label="No account"
                value={isLoading ? loadingValue : (metrics?.workforcesWithoutAccount ?? 0)}
                helper="Workforce without login"
                icon={<PersonOffRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>
          </Grid>
        </MotionBox>

        <MotionBox variants={filtersAnimation} initial="hidden" animate="visible">
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Typography
              sx={{
                color: "text.primary",
                fontSize: 15,
                fontWeight: 800,
              }}
            >
              User directory
            </Typography>

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              flexWrap="wrap"
              gap={1}
              alignItems={{
                xs: "stretch",
                sm: "center",
              }}
            >
              <TextField
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Search user, email or company..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded
                        sx={{
                          color: colors.orange.base,
                          fontSize: 17,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: {
                    xs: "100%",
                    sm: 300,
                  },
                  "& .MuiOutlinedInput-root": {
                    minHeight: 36,
                    borderRadius: "16px",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    fontSize: "0.8125rem",
                    transition: "all 150ms ease",

                    "& fieldset": {
                      borderColor: "var(--fx-surface-border, #E2E8F0)",
                    },

                    "&:hover fieldset": {
                      borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                    },

                    "&.Mui-focused": {
                      transform: "translateY(-1px)",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: colors.orange.base,
                    },
                  },
                }}
              />

              <Select
                value={confirmationFilter}
                disabled={recordTypeFilter === "workforces"}
                onChange={(event) => {
                  setConfirmationFilter(event.target.value as ConfirmationFilter);
                  setPage(0);
                }}
                sx={{
                  height: 36,
                  minWidth: 175,
                  borderRadius: "16px",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  fontSize: "0.8125rem",
                  transition: "transform 150ms ease",

                  "&:hover": {
                    transform: "translateY(-1px)",
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.gray[100],
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                    borderWidth: 1,
                  },
                }}
              >
                <MenuItem value="all">All account statuses</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="unconfirmed">Unconfirmed</MenuItem>
              </Select>

              <Select
                size="small"
                value={roleFilter}
                onChange={(event) => {
                  setRoleFilter(event.target.value);
                  setPage(0);
                }}
                sx={{
                  height: 36,
                  minWidth: 150,
                  borderRadius: "16px",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  fontSize: "0.8125rem",
                  transition: "transform 150ms ease",

                  "&:hover": {
                    transform: "translateY(-1px)",
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.gray[100],
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                    borderWidth: 1,
                  },
                }}
              >
                <MenuItem value="all">All roles</MenuItem>

                {availableRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {formatRole(role)}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                value={recordTypeFilter}
                onChange={(event) => {
                  const nextRecordType = event.target.value as RecordTypeFilter;

                  setRecordTypeFilter(nextRecordType);
                  setPage(0);

                  if (nextRecordType === "workforces") {
                    setConfirmationFilter("all");
                    setRoleFilter("all");
                  }
                }}
                sx={{
                  height: 36,
                  minWidth: 180,
                  borderRadius: "16px",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  fontSize: "0.8125rem",
                  transition: "transform 150ms ease",

                  "&:hover": {
                    transform: "translateY(-1px)",
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.gray[100],
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                    borderWidth: 1,
                  },
                }}
              >
                <MenuItem value="all">All records</MenuItem>
                <MenuItem value="users">User accounts</MenuItem>
                <MenuItem value="workforces">Workforce only</MenuItem>
              </Select>
            </Stack>
          </Stack>
        </MotionBox>

        <MotionPaper
          elevation={0}
          variants={tableAnimation}
          initial="hidden"
          animate="visible"
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            bgcolor: theme.palette.grey[50],
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <TableContainer
            sx={{
              minHeight: 320,
              maxHeight: {
                xs: 460,
                md: 520,
              },
              overflow: "auto",
            }}
          >
            {isLoading && users.length === 0 ? (
              <Box
                sx={{
                  height: 360,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : isError ? (
              <Box
                sx={{
                  height: 360,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Typography color="error">Error loading users</Typography>
              </Box>
            ) : (
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: "fixed",

                  opacity: isFetching ? 0.65 : 1,
                  transition: "opacity 150ms ease",
                }}
              >
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align as "left" | "center"}
                        sx={{
                          width: column.width,
                          minWidth: column.width,
                          maxWidth: column.width,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          fontSize: "0.6875rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: "text.secondary",
                          py: 1.5,
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {users.length > 0 ? (
                    <AnimatePresence mode="popLayout">
                      {users.map((user, index) => {
                        const userName = getUserName(user);
                        const isWorkforce = user.recordType === "workforce";
                        const confirmed = !isWorkforce && isUserConfirmed(user);
                        const companyNames = user.companies.map((company) => company.businessName).filter(Boolean);

                        return (
                          <MotionTableRow
                            key={user.uid}
                            layout
                            initial={{
                              opacity: 0,
                              y: 6,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              transition: {
                                delay: index * 0.035,
                                duration: 0.2,
                                ease: "easeOut",
                              },
                            }}
                            exit={{
                              opacity: 0,
                              x: -8,
                              transition: {
                                duration: 0.15,
                              },
                            }}
                            whileHover={{
                              backgroundColor: theme.palette.grey[50],
                              transition: {
                                duration: 0.12,
                              },
                            }}
                            whileTap={{
                              scale: 0.998,
                            }}
                            onClick={() => {
                              console.log("Selected user:", user);
                            }}
                            sx={{
                              cursor: "pointer",
                              bgcolor: "var(--fx-surface-base, #FFFFFF)",
                            }}
                          >
                            {/* User */}
                            <TableCell
                              sx={{
                                width: 120,
                                minWidth: 120,
                                maxWidth: 120,
                                py: 0.75,
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <MotionBox
                                  whileHover={{
                                    scale: 1.08,
                                    rotate: 2,
                                  }}
                                  transition={{
                                    duration: 0.15,
                                  }}
                                  sx={{
                                    flexShrink: 0,
                                  }}
                                >
                                  <Avatar
                                    src={user.avatar || undefined}
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
                                      color: "#C2410C",
                                      fontSize: 10,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {getUserInitials(user)}
                                  </Avatar>
                                </MotionBox>

                                <Box sx={{ minWidth: 0 }}>
                                  <Tooltip title={userName} placement="top" arrow enterDelay={500}>
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.8125rem",
                                        color: "text.primary",
                                        lineHeight: 1.25,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        cursor: "default",
                                      }}
                                    >
                                      {userName}
                                    </Typography>
                                  </Tooltip>

                                  {(isWorkforce || (user.useAliasAsDisplayName && user.alias)) && (
                                    <Typography
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: isWorkforce ? "#C2410C" : "text.secondary",
                                        lineHeight: 1.2,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {isWorkforce ? "No account" : "Display alias"}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </TableCell>

                            {/* Email */}
                            <TableCell
                              sx={{
                                width: 160,
                                minWidth: 160,
                                maxWidth: 160,
                                py: 0.75,
                                overflow: "hidden",
                              }}
                            >
                              <Tooltip title={user.email || ""} placement="top" arrow enterDelay={500}>
                                <Typography
                                  component="span"
                                  sx={{
                                    display: "block",
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    cursor: "default",
                                  }}
                                >
                                  {user.email || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            {/* Phone */}
                            <TableCell
                              sx={{
                                width: 90,
                                minWidth: 90,
                                maxWidth: 90,
                                py: 0.75,
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {user.phone || "—"}
                            </TableCell>

                            {/* Roles */}
                            <TableCell
                              sx={{
                                width: 80,
                                minWidth: 80,
                                maxWidth: 80,
                                py: 0.75,
                                overflow: "hidden",
                              }}
                            >
                              {user.roles.length > 0 ? (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  sx={{
                                    overflow: "hidden",
                                  }}
                                >
                                  {user.roles.slice(0, 2).map((role) => (
                                    <Chip
                                      key={role.id}
                                      label={formatRole(role.type)}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        maxWidth: 125,
                                        bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                                        color: "var(--fx-surface-textsecondary, #64748B)",
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        border: "none",

                                        "& .MuiChip-label": {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        },
                                      }}
                                    />
                                  ))}

                                  {user.roles.length > 2 && (
                                    <Chip
                                      label={`+${user.roles.length - 2}`}
                                      size="small"
                                      title={user.roles
                                        .slice(2)
                                        .map((role) => formatRole(role.type))
                                        .join(", ")}
                                      sx={{
                                        height: 22,
                                        minWidth: 32,
                                        bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
                                        color: "#C2410C",
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        border: "none",
                                      }}
                                    />
                                  )}
                                </Stack>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                  }}
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            {/* Companies */}
                            <TableCell
                              sx={{
                                width: 100,
                                minWidth: 100,
                                maxWidth: 100,
                                py: 0.75,
                              }}
                            >
                              {user.companies.length > 0 ? (
                                <Box sx={{ minWidth: 0 }}>
                                  <Tooltip title={companyNames.join(", ") || ""} placement="top" arrow enterDelay={500}>
                                    <Typography
                                      sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        color: "text.primary",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        cursor: "default",
                                      }}
                                    >
                                      {user.companies[0].businessName}
                                    </Typography>
                                  </Tooltip>

                                  {user.companies.length > 1 && (
                                    <Typography
                                      sx={{
                                        mt: 0.1,
                                        fontSize: "0.65rem",
                                        color: "text.secondary",
                                        lineHeight: 1.2,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      +{user.companies.length - 1} more
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                  }}
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            {/* Account status */}
                            <TableCell
                              align="center"
                              sx={{
                                width: 80,
                                minWidth: 80,
                                maxWidth: 80,
                                py: 0.75,
                              }}
                            >
                              <Chip
                                label={isWorkforce ? "No account" : confirmed ? "Confirmed" : "Unconfirmed"}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  bgcolor: isWorkforce ? "var(--fx-surface-muted, #F1F5F9)" : confirmed ? "var(--fx-colors-success-100, #D1FAE5)" : "var(--fx-colors-yellow-100, #FFF9C2)",
                                  color: isWorkforce ? "var(--fx-surface-textsecondary, #64748B)" : confirmed ? "#15803D" : "#B45309",
                                  border: "none",
                                }}
                              />
                            </TableCell>

                            {/* Created */}
                            <TableCell
                              align="center"
                              sx={{
                                width: 90,
                                minWidth: 90,
                                maxWidth: 90,
                                py: 0.75,
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDate(user.createdAt)}
                            </TableCell>
                          </MotionTableRow>
                        );
                      })}
                    </AnimatePresence>
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{
                          border: "none",
                          py: 10,
                        }}
                      >
                        <MotionBox
                          initial={{
                            opacity: 0,
                            y: 5,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                          }}
                          transition={{
                            duration: 0.22,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                            }}
                          >
                            No users match the selected filters
                          </Typography>
                        </MotionBox>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.totalItems ?? 0}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(_event, nextPage) => {
              setPage(nextPage);
            }}
            onRowsPerPageChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",

              "& .MuiTablePagination-toolbar": {
                minHeight: 38,
              },

              "& .MuiTablePagination-selectLabel": {
                fontSize: "0.8125rem",
              },

              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.8125rem",
              },

              "& .MuiIconButton-root": {
                transition: "transform 150ms ease, background-color 150ms ease",

                "&:hover": {
                  transform: "scale(1.06)",
                },
              },
            }}
          />
        </MotionPaper>
      </Box>
    </MotionBox>
  );
};
